/**
 * Tanjong Life · "Build the policy lapse prevention process by describing it."
 *
 * tl-pr13 is ALREADY on the shelf (Deployed v2 — RET-3303 is waiting at its gate with
 * seventy-four days of lead). The conversation assembles it on the open canvas from an
 * outline that mirrors the seeded doc (the test pins the mirror), so the two things that
 * make it a governed workflow arrive as choices rather than as scaffolding: the suppression
 * band, and the adviser gate. It ends by opening the deployed process.
 *
 * Keywords are compounded on `lapse`, `retention` and `at-risk`; no bare `claim`.
 */
import type { Scenario } from '../director.js';
import { outlineDoc, slicer, tlTrigger } from './tanjong-canvas.js';
import { MAKE_LAPSE } from './tanjong-make-intents.js';

const PA = { kind: 'agent', label: 'Persistency Analyst' } as const;

const DOC = outlineDoc({
  pk: 'tl-pr13',
  name: 'Policy lapse prevention',
  agent: {
    name: 'Retention Sentinel',
    role: 'Runs policy lapse prevention: three signals joined, the quiet band left alone, every send released by an adviser',
    handle: '@retention-sentinel',
    icon: 'badge',
    accent: 'green',
  },
  triggers: [
    tlTrigger(
      'trg-lapse-sweep',
      0,
      'Every night at 02:00 SGT, across the in-force book: a premium more than seven days overdue.',
    ),
    tlTrigger(
      'trg-policy-mandate-cancelled',
      1,
      'The earliest of the three signals, and the strongest: a GIRO mandate cancelled at the bank.',
    ),
  ],
  objective: 'tl-o2',
  metrics: ['tl-m-lapse-lead'],
  stages: [
    {
      name: 'Signals nobody read together',
      color: 'teal',
      steps: [
        {
          name: 'Read the premium payment cadence',
          type: 'application',
          app: 'policyledger',
          desc: 'From the policy ledger. A premium paid more than seven days late gives forty-one days of warning — and on its own means very little, because half the book pays late at least once a year.',
        },
        {
          name: 'Read the mandate status at the bank',
          type: 'application',
          app: 'policyledger',
          desc: 'A direct debit cancelled at the BANK rather than at the carrier gives seventy-four days of warning. The signal lives in the collections platform and has never been joined to the policy record.',
        },
        {
          name: 'Read whether the last statements were opened',
          type: 'application',
          app: 'whatsapp',
          desc: 'Two annual statements unopened. The weakest of the three signals, and the one that turns the other two from a billing problem into a relationship problem.',
        },
        {
          name: 'Join the three signals against one policy',
          type: 'script',
          desc: 'This box is the whole of what is new. Every signal above already existed inside the carrier, in three systems that do not read each other. Read together they give seventy-four days of warning before a fourteen-year policy lapses on a Tuesday.',
        },
      ],
    },
    {
      name: 'Rank, and decline to act',
      color: 'blue',
      steps: [
        {
          name: 'Rank the book by lapse risk',
          type: 'agent',
          performer: PA,
          guard: 'signals_joined',
          desc: 'Invokes Persistency Analyst. It orders the book; it contacts nobody and it cannot. The ranking is a queue for advisers, not a send list.',
        },
        {
          name: 'Compute the lead time before the grace period ends',
          type: 'script',
          desc: 'How many days there are to act. A flag with no lead time is a statistic; a flag with seventy-four days on it is a decision somebody can still make.',
        },
        {
          name: 'Suppress the band where the correct action is no action',
          type: 'script',
          desc: 'Roughly half the book. Declining to contact is a step on this canvas rather than an absence, because a system that contacts everybody it is unsure about is not a retention programme — it is a nuisance, and it is how a book learns to ignore you.',
        },
      ],
    },
    {
      name: 'Suitability',
      color: 'amber',
      milestone: {
        name: 'Ranked, and the quiet band left alone',
        desc: 'The book is ordered and the band where the right answer is silence is suppressed.',
        guard: 'in_scope',
      },
      steps: [
        {
          name: 'Draft the outreach for this household',
          type: 'agent',
          performer: PA,
          guard: 'in_scope',
          desc: 'One household, one draft, written against what the signals actually say. It is a draft and it stays one until a person releases it.',
        },
        {
          name: 'Read the suitability wording in force',
          type: 'agent',
          performer: PA,
          desc: 'Contacting a policyholder about a protection policy is a financial advisory action under MAS FEAT and Notice FAA-N16. The wording rules are the carrier’s own and are governed inside the knowledge base, not in a rules engine; they are read at the version in force.',
        },
        {
          name: 'Check the wording against FEAT and Notice FAA-N16',
          type: 'script',
          desc: 'Contacting a policyholder about a protection policy is a financial advisory action. The wording clears the check BEFORE an adviser sees it, so what reaches them is a decision about the household rather than a proofreading job.',
        },
      ],
    },
    {
      name: 'Adviser release',
      color: 'pink',
      steps: [
        {
          name: 'Release the outreach, or decline it',
          type: 'human',
          performer: { kind: 'group', label: 'Advisory Reviewers' },
          sla: { amount: '3', unit: 'd' },
          guard: 'suitability_passed',
          desc: 'The only gate. A named adviser reads the signals and the draft and decides — and declining is a first-class outcome recorded the same way as releasing, because the band where the right answer is silence is half the book.',
        },
        {
          name: 'Hold everything unsent until a named adviser has decided',
          type: 'script',
          desc: 'Not a queue that drains on a timer. If no adviser decides, nothing goes out — and the flag ages, which is itself a finding the Claims Integrity Sentinel watches for.',
        },
        {
          name: 'Record the adviser’s decision either way',
          type: 'script',
          desc: 'Released or declined, with the reason. A retention programme that only records its sends cannot tell you what it chose not to do, which is the half that protects the book.',
        },
      ],
    },
    {
      name: 'Contact & record',
      color: 'green',
      milestone: {
        name: 'Decided by a named adviser',
        desc: 'Released or declined — and recorded either way.',
        guard: 'decision_recorded',
      },
      steps: [
        {
          name: 'Send the outreach on the household’s own channel',
          type: 'application',
          app: 'whatsapp',
          guard: 'adviser_released',
          desc: 'The only activity that contacts anybody, and it cannot run until an adviser has released it. No agent holds this operation.',
        },
        {
          name: 'Write the retention record',
          type: 'script',
          desc: 'The three signals, the lead time, the suitability version, the adviser and what they decided. One record, whether the household was contacted or deliberately left alone.',
        },
        {
          name: 'Track the outcome against the flag',
          type: 'agent',
          performer: { kind: 'agent', label: 'Retention Sentinel' },
          desc: 'Did the policy persist. Measured against what the flag predicted rather than against whether the message was opened, because the point was never the message.',
        },
      ],
    },
  ],
});

const S = slicer(DOC);
const P_NAME = S.slice({ name: DOC.name, agent: DOC.agent });
const P_TRIGGERS = S.slice({ triggers: DOC.triggers });
const P_STAGE1 = S.stagePatch(0);
// Ranking lands first; the suppression band arrives only after the presenter has been
// asked what to do with the half of the book where the answer is nothing.
const s2 = S.stageAt(1);
const suppress = S.nodesIn(s2.id).find((n) => n.name.startsWith('Suppress'));
const P_RANK = S.stagePatch(1, suppress ? [suppress.id] : []);
const P_SUPPRESS = S.slice({ nodes: suppress ? [suppress] : [] });
const P_STAGE3 = S.stagePatch(2);
const P_GATE = S.stagePatch(3);
const P_STAGE5 = { ...S.stagePatch(4), objective: DOC.objective ?? null, metrics: DOC.metrics ?? [] };

export const tanjongBuildLapse: Scenario = {
  id: 'tanjong-build-lapse',
  orgId: 'tanjong-life',
  keywords: [
    'lapse prevention',
    'policy lapse',
    'prevent lapse',
    'lapse risk',
    'at-risk policies',
    'retention outreach',
    'stop policies lapsing',
  ],
  intents: [MAKE_LAPSE],
  steps: [
    {
      do: 'say',
      text: 'The interesting thing about this one is that you already have everything it needs. Nothing below is a new data source — it is three you already own, read together.',
      delay: 550,
    },
    {
      do: 'canvas',
      patch: P_NAME,
      summary: 'Named it Policy lapse prevention · Retention Sentinel set as the driver',
      delay: 700,
    },
    {
      do: 'insight',
      title: 'Three signals, in three systems that do not read each other',
      body: 'A premium paid more than seven days late — forty-one days of warning. A GIRO mandate cancelled at the BANK rather than at you — seventy-four days. Two annual statements unopened — on its own, almost nothing. Not one of these means much alone, which is exactly why nobody joined them. Read together they give you seventy-four days before a fourteen-year policy lapses on a Tuesday.',
      sources: ['Policy & benefits', 'GIRO mandate events', 'Statement engagement'],
      delay: 1200,
    },
    {
      do: 'canvas',
      patch: P_TRIGGERS,
      summary: 'Added 2 triggers — nightly at-risk sweep · GIRO mandate cancelled',
      delay: 650,
    },
    {
      do: 'canvas',
      patch: P_STAGE1,
      summary: 'Stage 1 · Signals nobody read together — 4 activities',
      delay: 850,
    },
    {
      do: 'say',
      text: 'The fourth box is the whole of what is new here. Everything above it already existed inside the insurer.',
      delay: 700,
    },
    {
      do: 'canvas',
      patch: P_RANK,
      summary: 'Stage 2 · Rank the book, with lead time on every row',
      delay: 800,
    },
    {
      do: 'ask',
      id: 'band',
      prompt:
        'Roughly half the book will rank in a band where the signals are weak. What should happen to it?',
      options: [
        { id: 'suppress', label: 'Leave it alone, and record that decision (recommended)' },
        { id: 'contact', label: 'Contact them too — better safe' },
        { id: 'watch', label: 'Re-check it weekly without contact' },
      ],
      delay: 750,
    },
    {
      do: 'branch',
      cases: {
        contact: [
          {
            do: 'say',
            text: 'I would push back. A system that contacts everybody it is unsure about is not a retention programme, it is a nuisance — and it is how a book learns to ignore you. I will suppress that band and you can lower the threshold once you have seen the persistency numbers.',
            delay: 900,
          },
        ],
        watch: [
          {
            do: 'say',
            text: 'Re-check without contact — reasonable. I will suppress the outreach and leave the band in the sweep, so it re-ranks every night.',
            delay: 700,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Left alone, and recorded. Declining to contact is a step on this canvas rather than an absence, because a programme that only records its sends cannot tell you what it chose not to do.',
          delay: 700,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P_SUPPRESS,
      summary: 'Suppression band added — about 48% of the book',
      delay: 800,
    },
    {
      do: 'insight',
      title: 'And now the part that makes this a regulated workflow',
      body: 'Contacting a policyholder about a protection policy is a financial advisory action under MAS FEAT and Notice FAA-N16. So the wording clears a suitability check against your own rules BEFORE an adviser ever sees it — which means what reaches them is a decision about a household rather than a proofreading job.',
      sources: ['Suitability wording · governed in the knowledge base', 'MAS FEAT · Notice FAA-N16'],
      delay: 1200,
    },
    {
      do: 'canvas',
      patch: P_STAGE3,
      summary: 'Stage 3 · Suitability — draft, wording read, checked · milestone M1',
      delay: 850,
    },
    {
      do: 'askText',
      id: 'adviser',
      prompt: 'Who releases an outreach?',
      placeholder: 'e.g. the Advisory Reviewers',
      delay: 700,
    },
    {
      do: 'say',
      text: 'That group owns the gate, with a three-day target — and no timer that sends if nobody acts. If no adviser decides, nothing goes out, and the flag ages. That ageing is itself a finding, and it is the third thing the Claims Integrity Sentinel watches for.',
      delay: 900,
    },
    {
      do: 'canvas',
      patch: P_GATE,
      summary: 'Stage 4 · Adviser release — the single gate, and it holds',
      delay: 850,
    },
    {
      do: 'canvas',
      patch: P_STAGE5,
      summary: 'Stage 5 · Contact & record — 3 activities · milestone M2',
      delay: 800,
    },
    {
      do: 'say',
      text: 'Sixteen activities, one gate, and exactly one box that can contact anybody — guarded on the adviser’s release, and held by no agent. Testing now.',
      delay: 850,
    },
    {
      do: 'test',
      title: 'Testing components…',
      items: [
        { label: 'Nightly sweep', sub: 'The in-force book, 02:00 Singapore' },
        { label: 'Mandate signal', sub: 'Cancelled at the bank resolves from the policy feed' },
        { label: 'Signal join', sub: 'Three sources against one policy record' },
        { label: 'Lead-time computation', sub: 'Days before the grace period ends' },
        { label: 'Suppression band', sub: 'The band where no action is correct is excluded' },
        { label: 'Suitability check', sub: 'FEAT & Notice FAA-N16 wording pass' },
        { label: 'Adviser gate', sub: 'Advisory Reviewers · 3-day target · no send timer' },
        { label: 'Decline path', sub: 'Recorded the same way as a release' },
        { label: 'Send guard', sub: 'Outreach cannot run without adviser_released' },
      ],
      result: 'All components passed',
      delay: 600,
    },
    {
      do: 'say',
      text: 'All passed — including the one worth saying out loud: there is no path through this process that contacts a policyholder without a named adviser deciding first.',
      delay: 6200,
    },
    {
      do: 'say',
      text: 'This one is already on the shelf as v2. RET-3303 is at its adviser gate now, with seventy-four days of lead — and about half of what the sweep finds it deliberately leaves alone.',
      delay: 900,
    },
    {
      do: 'link',
      label: 'Open Policy lapse prevention — already deployed, v2',
      href: '/processes/tl-pr13',
      delay: 600,
    },
  ],
};
